import { UIPermission } from "../interface/type/ui-permission.type";
import { BackendPermission } from "./backend-permission.enum";

    /**
     * @description
     *  Wrapper de permisos de UI. Provee inmutabilidad y funciones útiles
     *  para consultar los permisos y sus controles asociados.
     */

export class UIPermissionWrapper {

    constructor(private uiPermission: UIPermission) {}

    /**
     * @description
     *  Computa los permisos asignados para el control de UI especificado, en
     *  caso de que este exista para la vista actual.
     *
     * @param uiControl
     *  El nombre clave del control de UI.
     *
     * @returns
     *  Un vector de permisos, donde cada permiso está asociado a un permiso
     *  nativo provisto por el backend.
     */
    public for(uiControl: string): BackendPermission[] {
        const permissions: BackendPermission[] = this.uiPermission[uiControl];
        if (permissions === undefined) {
            throw new Error(
                `El control de UI especificado no posee permisos definidos: '${uiControl}'.`);
        }
        else {
            return permissions;
        }
    }

    /**
     * @description
     *  Mezcla dos contenedores de permisos en un nuevo contenedor. El nuevo
     *  contenedor se construye mediante una copia profunda de los originales,
     *  con lo cual, las modificaciones sobre cada objeto son independientes.
     *
     *  Si una clave de control de UI se encuentra en ambos contenedores, el
     *  nuevo contenedor contendrá dicha clave con los permisos de ambos
     *  contenedores, sin repeticiones.
     *
     *  Si una clave no se encontraba en el contenedor original, entonces se
     *  agrega en el nuevo, sin repeticiones.
     *
     *  Nótese que si el contenedor original poseía permisos duplicados,
     *  entonces ocurrirán duplicados en el contenedor final. La deduplicación
     *  aplica únicamente sobre el agregado.
     *
     * @param uiPermissionWrapper
     *  El contenedor de permisos con el cual realizar la mezcla.
     *
     * @returns
     *  Un nuevo contenedor con los permisos del contenedor original y el
     *  agregado.
     */
    public merge(uiPermissionWrapper: UIPermissionWrapper): UIPermissionWrapper {
        const uiPermission: UIPermission = {};
        for (const [key, permissions] of Object.entries(this.uiPermission)) {
            uiPermission[key] = [ ...permissions ];
        }
        for (const [key, permissions] of Object.entries(uiPermissionWrapper.uiPermission)) {
            const uniquePermissions: Set<BackendPermission> = new Set<BackendPermission>();
            if (uiPermission[key] === undefined) {
                uiPermission[key] = [];
                permissions.forEach(permission => uniquePermissions.add(permission));
                uniquePermissions.forEach(permission => uiPermission[key].push(permission));
            }
            else {
                uiPermission[key].forEach(permission => uniquePermissions.add(permission));
                permissions.filter(permission => !uniquePermissions.has(permission))
                    .forEach(permission => uiPermission[key].push(permission));
            }
        }
        return new UIPermissionWrapper(uiPermission);
    }
}
